import Tour from '@core/components/Tour';
import { useApiUtils } from '@core/util/apiUtils';
import FusePageCarded from '@fuse/core/FusePageCarded';
import notaFiscalApi from 'app/services/api/notaFiscalApi';
import { usuarioApi } from 'app/services/api/usuarioApi';
import * as Actions from 'app/store/actions';
import { GlobalContext } from 'app/store/globalcontext';
import { initializeReactGA } from 'app/utils/analyticsUtil';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import { NotaFiscalContext, NotaFiscalProvider } from './NotaFiscalContext';
import { NotaFiscalSearchHeader, NotaFiscalTable } from './components';
import NotaFiscalBuscaAvancadaModal from './components/Modal/NotaFiscalBuscaAvancada/NotaFiscalBuscaAvancadaModal';
import NotaFiscalDevolucaoModal from './components/NotaFiscalDevolucao/NotaFiscalDevolucaoModal';
import NotaFiscalRemessaModal from './components/notaFiscalRemessa/NotaFiscalRemessaModal';
import { Nfsteps } from './components/steps/steps';

const NotaFiscal = () => {
  const [, setLoading] = useContext(NotaFiscalContext).loading;
  const [, setNotasFiscais] = useContext(NotaFiscalContext).notasFiscais;
  const [data] = useContext(NotaFiscalContext).data;
  const [, setSelecao] = useContext(NotaFiscalContext).selecao;
  const [search, setSearch] = useContext(NotaFiscalContext).search;
  const [, setTotalElements] = useContext(NotaFiscalContext).totalElements;
  const [, setPage] = useContext(NotaFiscalContext).page;
  const [, setSize] = useContext(NotaFiscalContext).size;
  const [, setSomatorios] = useContext(NotaFiscalContext).somatorios;
  const [atualizarGrid, setAtualizarGrid] = useContext(NotaFiscalContext).atualizarGrid;
  const [filtroAvancado] = useContext(NotaFiscalContext).filtroAvancado;
  const [funcionalidadesTour, setFuncionalidadesTour] = useContext(GlobalContext).funcionalidadesTour;

  const dispatch = useDispatch();
  const apiUtils = useApiUtils(dispatch, Actions);

  const [run, setRun] = useState(false);
  const [tour, setTour] = useState(false);

  const history = useHistory();

  const initialPage = {
    mesCompetencia: data,
    page: 0,
    q: '',
    size: 15,
    sort: 'dataEmissaoNf,DESC',
  };

  const fetchData = (s) => {
    s.mesCompetencia = data;
    s.q = search.q;
    fetchNotasFiscais(s);
  };

  const fetchSomatorio = async (search) => {
    const response = await notaFiscalApi.somatorio(search);
    setSomatorios(response.data);
  };

  const handleSelectionChange = (selecao) => {
    setSelecao(selecao);
  };

  const formatarFiltroAvancado = (filtro) => {
    return {
      ...filtro,
      periodoEmissaoInicio: filtro?.periodoEmissaoInicio
        ? filtro?.periodoEmissaoInicio.format('YYYY-MM-DD')
        : undefined,
      periodoEmissaoFim: filtro?.periodoEmissaoFim ? filtro?.periodoEmissaoFim.format('YYYY-MM-DD') : undefined,
      servicoId: filtro?.servico?.id,
      produtoId: filtro?.produto?.id,
      emCancelamento: filtro?.situacao === 'EM_CANCELAMENTO',
      erroCancelamento: filtro?.situacao === 'ERRO_CANCELAMENTO',
    };
  };

  const fetchNotasFiscais = async (search = initialPage) => {
    setLoading(true);
    try {
      if (!search?.filtroAvancado) {
        search.filtroAvancado = formatarFiltroAvancado(filtroAvancado);
      } else {
        search.filtroAvancado = formatarFiltroAvancado(search.filtroAvancado);
      }

      delete search.filtroAvancado.servico;

      if (
        (search.filtroAvancado && search.filtroAvancado.situacao === 'EM_CANCELAMENTO') ||
        search.filtroAvancado.situacao === 'ERRO_CANCELAMENTO'
      ) {
        delete search.filtroAvancado.situacao;
      }

      const funcionalidadeJaExistente = funcionalidadesTour?.some((e) => e === 'NOTAS_FISCAIS');
      if (!funcionalidadeJaExistente) {
        setRun(true);
        setTour(true);
      }
      const response = await notaFiscalApi.buscar(search);
      setPage(response.data.number);
      setSize(response.data.size);
      setTotalElements(response.data.totalElements);
      setNotasFiscais(response.data.content);
      fetchSomatorio({ tipoSelecao: 'NENHUM', ids: [], ...search });
    } catch (error) {
      apiUtils.handleApiError(error);
    } finally {
      setLoading(false);
      setAtualizarGrid(false);
    }
  };

  const onInit = () => {
    initializeReactGA(history?.location?.pathname);
    if (!funcionalidadesTour) {
      setFuncionalidadesTour([]);
    }
    fetchNotasFiscais();
  };

  useEffect(onInit, []);

  useEffect(() => {
    if (atualizarGrid) {
      onInit();
    }
    // A função é recriada a cada render; incluí-la reexecutaria a busca em loop.
    // O efeito depende apenas do gatilho listado.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [atualizarGrid]);

  const atualizarTour = async () => {
    try {
      setLoading(true);
      funcionalidadesTour.push('NOTAS_FISCAIS');
      await usuarioApi.updateTour(funcionalidadesTour);
      setRun(false);
      setTour(false);
      setFuncionalidadesTour(funcionalidadesTour);
      fetchNotasFiscais();
    } catch (err) {
      apiUtils.handleApiError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FusePageCarded
      innerScroll
      header={<NotaFiscalSearchHeader onChange={setSearch} />}
      content={
        <>
          <Tour steps={Nfsteps} run={run} setRun={setRun} onFinish={() => atualizarTour()} disableScrolling={true} />
          <NotaFiscalBuscaAvancadaModal fetchNotasFiscais={fetchNotasFiscais} />
          <NotaFiscalTable onSelection={handleSelectionChange} fetchData={fetchData} onTour={tour} />
          <NotaFiscalDevolucaoModal />
          <NotaFiscalRemessaModal />
        </>
      }
    />
  );
};

const NotaFiscalContainer = () => (
  <NotaFiscalProvider>
    <NotaFiscal />
  </NotaFiscalProvider>
);

export default NotaFiscalContainer;
